import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Tag } from 'lucide-react';
import { ProductThumbnail } from '@/components/products/ProductThumbnail';
import { cn } from '@/lib/utils';
import { getProductSlug } from '@/lib/product-url';
import { shouldBlurSensitiveImage } from '@/lib/sensitive-content';

export function suggestionProductThumbUrl(product) {
  let u = product?.images?.[0] || '';
  if (!u || typeof u !== 'string') return '';
  if (u.startsWith('/uploads/')) u = u.replace('/uploads/', '/api/uploads/');
  return u;
}

/** Arama önerisinde sorgu ile eşleşen kısmı kalın göster */
export function highlightQueryMatch(text, query) {
  if (text == null || text === '') return null;
  const t = String(text);
  const qVal = (query || '').trim();
  if (!qVal) return t;
  const lower = t.toLowerCase();
  const qi = qVal.toLowerCase();
  const idx = lower.indexOf(qi);
  if (idx === -1) {
    return <span className="text-foreground/90">{t}</span>;
  }
  return (
    <span className="text-foreground/90">
      {t.slice(0, idx)}
      <strong className="font-semibold text-foreground">{t.slice(idx, idx + qVal.length)}</strong>
      {t.slice(idx + qVal.length)}
    </span>
  );
}

/**
 * Header / mobil arama: kategori + ürün önerileri (üründe ilk görsel).
 */
export function SearchSuggestionsDropdown({
  panelOpen,
  queryTrimmed,
  isSearching,
  matchedCategories,
  searchResults,
  onProductPick,
  onCategoryPick,
  className,
  listFooter,
  /** 'dropdown' = header altı açılır; 'sheet' = tam ekran mobil panel gövdesi */
  variant = 'dropdown',
}) {
  const { t } = useTranslation();
  const q = queryTrimmed;
  const hasRows = matchedCategories.length > 0 || searchResults.length > 0;
  const isSheet = variant === 'sheet';

  if (!panelOpen) return null;

  return (
    <div
      className={cn(
        'bg-background flex flex-col overflow-hidden',
        isSheet
          ? 'relative flex-1 min-h-0 w-full'
          : 'absolute top-full left-0 right-0 z-[102] mt-px border border-t-0 border-orange-500 rounded-b-2xl shadow-lg max-h-[min(70vh,440px)]',
        className,
      )}
      data-testid="search-suggestions-panel"
    >
      {isSearching && !hasRows ? (
        <div className="p-6 flex justify-center text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : !hasRows ? (
        <div className="p-4 text-center text-muted-foreground text-sm">
          {t('search.noResults', 'Sonuç bulunamadı')}
        </div>
      ) : (
        <>
          <div className="px-3 py-2 border-b border-orange-100 bg-orange-50/60 shrink-0">
            <p className="text-xs font-bold text-foreground tracking-wide">
              {t('search.relatedResults', 'İlgili Sonuçlar')}
            </p>
          </div>
          <ul
            className={cn(
              'py-1 divide-y divide-border overscroll-contain flex-1 min-h-0',
              isSheet ? 'overflow-y-auto touch-pan-y' : 'overflow-y-auto',
            )}
          >
            {matchedCategories.map((c) => (
              <li key={`sug-cat-${c.id}`}>
                <button
                  type="button"
                  onClick={() => onCategoryPick(c.slug)}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-orange-50/80 flex items-center gap-3 transition-colors"
                >
                  <div
                    className="h-11 w-11 shrink-0 rounded-md bg-orange-50 border border-orange-100 flex items-center justify-center text-orange-600"
                    aria-hidden
                  >
                    <Tag className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-[10px] font-semibold uppercase text-orange-600 block mb-0.5">
                      {t('search.category', 'Kategori')}
                    </span>
                    <span className="leading-snug">{highlightQueryMatch(c.name, q)}</span>
                  </div>
                </button>
              </li>
            ))}
            {searchResults.map((product) => {
              const thumb = suggestionProductThumbUrl(product);
              return (
                <li key={`sug-p-${product.id}`}>
                  <button
                    type="button"
                    onClick={() => onProductPick(getProductSlug(product) || product.id)}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted/70 flex items-center gap-3 transition-colors"
                  >
                    <ProductThumbnail
                      src={thumb}
                      alt=""
                      className="h-11 w-11 shrink-0 rounded-md border border-border"
                      sensitive={shouldBlurSensitiveImage(product)}
                    />
                    <div className="min-w-0 flex-1 flex flex-col">
                      <span className="text-[10px] font-semibold uppercase text-primary/80 mb-0.5">
                        {t('search.product', 'Ürün')}
                      </span>
                      <span className="leading-snug line-clamp-2">{highlightQueryMatch(product.name, q)}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          {listFooter ? <div className="shrink-0 border-t border-border">{listFooter}</div> : null}
        </>
      )}
    </div>
  );
}
